import { useState } from "react";
import { Box, Typography, Button, IconButton, Divider, Tooltip, Paper, Stack } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SmartphoneIcon from "@mui/icons-material/Smartphone";

const backToLoginSx = {
	fontSize: "0.78rem",
	fontWeight: 600,
	color: "#8097b8",
	textTransform: "none",
	bgcolor: "transparent !important",
	"&:hover": { bgcolor: "transparent !important", color: "#3A7DC9", textDecoration: "underline" },
};

const QRStep = ({ qrCode, secretString, onNext, onBackToLogin }) => {
	const [copied, setCopied] = useState(false);

	const qrSrc = qrCode?.startsWith("data:") ? qrCode : `data:image/png;base64,${qrCode}`;

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(secretString);
		} catch {
			const el = document.createElement("textarea");
			el.value = secretString;
			document.body.appendChild(el);
			el.select();
			document.execCommand("copy");
			document.body.removeChild(el);
		}
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Box
			sx={{
				width: "100%",
				bgcolor: "#ffffff",
				borderRadius: "8px",
				overflow: "hidden",
				border: "1px solid #e8eef6",
			}}
		>
			{/* ── Top header bar ── */}
			<Box
				sx={{
					px: 3,
					py: 2.5,
					borderBottom: "1px solid #f0f4fa",
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					width: "75% !important",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: 36,
						height: 36,
						borderRadius: "9px",
						bgcolor: "#EBF3FF",
						color: "#3A7DC9",
					}}
				>
					<ShieldOutlinedIcon sx={{ fontSize: 20 }} />
				</Box>
				<Box>
					<Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a2f5a", lineHeight: 1.2 }}>
						Two-Factor Authentication Setup
					</Typography>
					<Typography sx={{ fontSize: "0.75rem", color: "#8097b8", mt: 0.25 }}>
						Link your authenticator app to secure your account
					</Typography>
				</Box>
			</Box>

			{/* ── Two-column body ── */}
			<Stack direction={{ xs: "column", sm: "row" }} sx={{ minHeight: 320 }}>
				{/* Left — QR + manual key */}
				<Box
					sx={{
						flex: "0 0 auto",
						width: { xs: "100%", sm: 260 },
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 2,
						px: 3,
						py: 3,
						borderRight: { xs: "none", sm: "1px solid #f0f4fa" },
						borderBottom: { xs: "1px solid #f0f4fa", sm: "none" },
						bgcolor: "#fafcff",
					}}
				>
					{/* Step pill */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.75,
							bgcolor: "#EBF3FF",
							color: "#3A7DC9",
							borderRadius: "20px",
							px: 1.5,
							py: 0.5,
							alignSelf: "flex-start",
						}}
					>
						<QrCode2Icon sx={{ fontSize: 14 }} />
						<Typography sx={{ fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.04em" }}>STEP 1 — SCAN</Typography>
					</Box>

					{/* QR frame */}
					{qrCode && (
						<Box
							sx={{
								p: "10px",
								bgcolor: "#ffffff",
								borderRadius: "12px",
								border: "1px solid #dce8f7",
								boxShadow: "0 2px 12px rgba(58,125,201,0.08)",
							}}
						>
							<Box
								component="img"
								src={qrSrc}
								alt="MFA QR Code"
								sx={{ display: "block", width: 160, height: 160, imageRendering: "pixelated" }}
							/>
						</Box>
					)}

					{/* Divider */}
					<Divider sx={{ width: "100%", "&::before, &::after": { borderColor: "#e4ecf7" } }}>
						<Typography sx={{ fontSize: "0.68rem", color: "#a0b0c8", textTransform: "uppercase", letterSpacing: "0.07em" }}>
							or manually
						</Typography>
					</Divider>

					{/* Secret key block */}
					<Paper
						elevation={0}
						sx={{
							width: "100%",
							bgcolor: "#f0f5fc",
							border: "1px solid #dce8f7",
							borderRadius: "10px",
							overflow: "hidden",
						}}
					>
						<Typography
							sx={{
								display: "block",
								px: 1.5,
								py: 0.75,
								fontSize: "0.65rem",
								fontWeight: 700,
								color: "#7a92b8",
								textTransform: "uppercase",
								letterSpacing: "0.09em",
								borderBottom: "1px solid #dce8f7",
							}}
						>
							Secret Key
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25 }}>
							<Typography
								component="code"
								sx={{
									flex: 1,
									fontFamily: "'Courier New', Courier, monospace",
									fontSize: "0.78rem",
									color: "#2d4f8a",
									letterSpacing: "0.1em",
									wordBreak: "break-all",
									lineHeight: 1.7,
									fontWeight: 600,
								}}
							>
								{secretString}
							</Typography>
							<Tooltip title={copied ? "Copied!" : "Copy key"} placement="top">
								<IconButton
									size="small"
									onClick={handleCopy}
									sx={{
										flexShrink: 0,
										width: 30,
										height: 30,
										borderRadius: "7px",
										bgcolor: copied ? "rgba(34,197,94,0.1) !important" : "rgba(58,125,201,0.1) !important",
										color: copied ? "#16a34a" : "#3A7DC9",
										"&:hover": {
											bgcolor: copied ? "rgba(34,197,94,0.18) !important" : "rgba(58,125,201,0.18) !important",
										},
										"& svg": { fontSize: 15 },
									}}
								>
									{copied ? <CheckIcon /> : <ContentCopyIcon />}
								</IconButton>
							</Tooltip>
						</Box>
					</Paper>
				</Box>

				{/* Right — instructions + CTA */}
				<Box
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						px: 3,
						py: 3,
						gap: 3,
					}}
				>
					{/* Step pill */}
					<Box>
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								gap: 0.75,
								bgcolor: "#F0FDF4",
								color: "#16a34a",
								borderRadius: "20px",
								px: 1.5,
								py: 0.5,
								mb: 2,
							}}
						>
							<SmartphoneIcon sx={{ fontSize: 14 }} />
							<Typography sx={{ fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.04em" }}>STEP 2 — VERIFY</Typography>
						</Box>

						<Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a2f5a", mb: 1 }}>Open your authenticator app</Typography>

						{/* Instruction steps */}
						{[
							{ n: "1", text: "Open Google Authenticator, Authy, or any TOTP app on your phone." },
							{ n: "2", text: 'Tap "Add account" or the + icon, then choose "Scan QR code".' },
							{ n: "3", text: "Point your camera at the QR code on the left, or enter the secret key manually." },
							{ n: "4", text: "Once added, a 6-digit code will appear — it refreshes every 30 seconds." },
						].map(({ n, text }) => (
							<Box key={n} sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
								<Box
									sx={{
										flexShrink: 0,
										width: 22,
										height: 22,
										borderRadius: "50%",
										bgcolor: "#EBF3FF",
										color: "#3A7DC9",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										fontSize: "0.7rem",
										fontWeight: 700,
										mt: 0.1,
									}}
								>
									{n}
								</Box>
								<Typography sx={{ fontSize: "0.8rem", color: "#4a6080", lineHeight: 1.6 }}>{text}</Typography>
							</Box>
						))}
					</Box>

					{/* Bottom hint + CTA */}
					<Box>
						<Box
							sx={{
								bgcolor: "#fffbeb",
								border: "1px solid #fde68a",
								borderRadius: "8px",
								px: 1.5,
								py: 1,
								mb: 2,
							}}
						>
							<Typography sx={{ fontSize: "0.74rem", color: "#92400e", lineHeight: 1.5 }}>
								⚠️ Keep your secret key safe. You'll need it to recover access if you lose your phone.
							</Typography>
						</Box>

						<Button
							fullWidth
							variant="contained"
							onClick={onNext}
							sx={{
								py: 1.25,
								fontWeight: 700,
								fontSize: "0.88rem",
								bgcolor: "#3A7DC9",
								borderRadius: "9px",
								textTransform: "none",
								boxShadow: "0 2px 8px rgba(58,125,201,0.25)",
								letterSpacing: "0.01em",
								"&:hover": { bgcolor: "#2d6ab5", boxShadow: "0 4px 14px rgba(58,125,201,0.35)" },
							}}
						>
							I've scanned it — Continue
						</Button>

						<Box sx={{ textAlign: "center", mt: 1 }}>
							<Button onClick={onBackToLogin} sx={backToLoginSx}>
								Back to login
							</Button>
						</Box>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};

export default QRStep;
